import { Component, inject, signal } from '@angular/core';
import {
	FormControl,
	FormGroup,
	FormsModule,
	ReactiveFormsModule,
} from '@angular/forms';
import { DestroyService } from '@context/frontend/common';
import { IsPlatformPipe } from '@context/frontend/native/platform';
import { GlobalSearchService } from '@context/frontend/search';
import { AlgoliaGlobal } from '@context/shared/types/search';
import { TranslocoModule } from '@jsverse/transloco';
import { TuiDropdown, TuiInput } from '@taiga-ui/core';
import { TuiSearchComponent } from '@taiga-ui/layout';
import { debounceTime, takeUntil } from 'rxjs';
import { HotkeyComponent } from '../hotkey/hotkey.component';
import { GlobalSearchDropdownComponent } from './global-search-dropdown/global-search-dropdown.component';

@Component({
	imports: [
		TuiSearchComponent,
		ReactiveFormsModule,
		FormsModule,
		TranslocoModule,
		TuiInput,
		GlobalSearchDropdownComponent,
		TuiDropdown,
		IsPlatformPipe,
		HotkeyComponent,
	],
	selector: 'ctx-global-search-bar',
	templateUrl: 'global-search-bar.component.html',
	styleUrl: 'global-search-bar.component.scss',
	providers: [DestroyService],
})
export class GlobalSearchBarComponent {
	readonly search = new FormGroup({
		term: new FormControl(''),
	});

	readonly destroy$ = inject(DestroyService);
	readonly searchService = inject(GlobalSearchService);

	readonly dropdownOpen = signal(false);

	readonly loading = signal(false);
	readonly data = signal<AlgoliaGlobal[]>([]);
	readonly totalElements = signal(0);
	readonly focused = signal(false);

	constructor() {
		this.initialize();
	}

	initialize() {
		this.search
			.get('term')
			?.valueChanges.pipe(takeUntil(this.destroy$), debounceTime(500))
			.subscribe((term) => {
				if (!term) {
					this.onReset();
					this.dropdownOpen.set(false);
					return;
				}

				this.fetchData(term);
			});
	}

	fetchData(term: string) {
		this.loading.set(true);
		this.searchService
			.fetch(term)
			.then((res) => {
				this.totalElements.set(res.nbHits ?? 0);
				this.data.set(res.hits ?? []);
				this.dropdownOpen.set(true);
			})
			.finally(() => {
				this.loading.set(false);
			});
	}

	onReset() {
		this.totalElements.set(0);
		this.data.set([]);
	}

	onClose() {
		this.dropdownOpen.set(false);
		this.search.get('term')?.setValue(null);
	}

	onFocus() {
		this.focused.set(true);
	}

	onBlur() {
		this.focused.set(false);
	}
}
